import { Box, Typography, useTheme } from '@mui/material';
import { DocumentUpload } from 'iconsax-react';
import type { ChangeEvent, DragEvent, FC, KeyboardEvent } from 'react';
import { useRef, useState } from 'react';

import { ALLOWED_ATTACHMENT_ACCEPT_STRING } from '@core/constants/app';
import { hasValue } from '@core/utils/equality';

const DROPZONE_ICON_SIZE = 32;

// Plain <input> doesn't support the sx prop, so this stays a style object
// (same visually-hidden-but-focusable technique as EmailAttachmentsSection.tsx).
const HIDDEN_INPUT_STYLE = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

type Props = {
  onFilesSelected: (files: File[]) => void;
};

export const FileDropzone: FC<Props> = ({ onFilesSelected }) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  // dragEnter/dragLeave also fire for child elements; track depth so the
  // highlight only clears when the pointer leaves the whole zone.
  const dragDepthRef = useRef(0);

  const openPicker = (): void => {
    inputRef.current?.click();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? []);
    // Reset so the same file(s) can be re-selected after removal/rejection.
    if (hasValue(inputRef.current)) {
      inputRef.current.value = '';
    }
    if (files.length > 0) {
      onFilesSelected(files);
    }
  };

  return (
    <Box
      role='button'
      tabIndex={0}
      aria-label='Drag and drop files here, or click to browse'
      data-drag-active={isDragActive}
      onClick={openPicker}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: 1.5,
        border: '2px dashed',
        borderColor: isDragActive ? theme.palette.SlabIndigo[500] : theme.palette.SlabGray[300],
        backgroundColor: isDragActive
          ? theme.palette.SlabIndigo[100]
          : theme.palette.background.paper,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          borderColor: theme.palette.SlabIndigo[500],
          backgroundColor: theme.palette.SlabIndigo[100],
        },
      }}
    >
      <DocumentUpload
        variant='TwoTone'
        color={theme.palette.SlabIndigo[700]}
        size={DROPZONE_ICON_SIZE}
      />
      <Typography variant='body2' fontWeight={600}>
        Drag and drop files here, or click to browse
      </Typography>
      <Typography variant='secondary'>Works with PDF, DOC/DOCX, XLS/XLSX, JPG/PNG, TXT</Typography>
      <input
        ref={inputRef}
        type='file'
        multiple
        accept={ALLOWED_ATTACHMENT_ACCEPT_STRING}
        onChange={handleInputChange}
        onClick={(event) => event.stopPropagation()}
        style={HIDDEN_INPUT_STYLE}
      />
    </Box>
  );
};
